import { Fragment, useEffect, useState } from "react";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModalModern from "../ModalModern";
import ModernPopupBody from "../common/ModernPopupBody";
import ProgressBar from "../../charts/progress/ProgressBar";
import BudgetHistoryChart from "../../charts/finances/BudgetHistoryChart";
import { primarySpendingCategories } from "../../../../utils/constants/plaidCategoryTaxonomy";
import {
  fetchBudgetTransactions,
  fetchBudgetSnapshotDetails,
} from "../../../../api/finances/budgetAPI";
import LoadingToRealTransitionWrapper from "../../transitions/LoadingToRealTransitionWraper";
import GhostCardContainer from "../../loading/skeletons/GhostCardContainer";
import GhostBar from "../../loading/skeletons/GhostBar";
import CollapsibleCardContainer from "../../accordions/CollapsibleCardContainer";
import TransactionRowCard from "../../cards/finances/TransactionRowCard";
import { getInitialsIconSrc } from "../../../../utils/images/iconUtils";

const parseBudgetNumber = (value, fallback = 0) => {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : fallback;
  if (typeof value !== "string") return fallback;

  const cleanedValue = value.trim().replace(/[^\d.,-]/g, "");
  if (!cleanedValue) return fallback;

  const hasComma = cleanedValue.includes(",");
  const hasDot = cleanedValue.includes(".");
  const commaParts = cleanedValue.split(",");
  const normalizedValue =
    hasComma && !hasDot && commaParts.length === 2 && commaParts[1].length < 3
      ? cleanedValue.replace(",", ".")
      : cleanedValue.replace(/,/g, "");
  const parsedValue = Number.parseFloat(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const normalizeThreshold = (value) => {
  const parsedThreshold = parseBudgetNumber(value, 0.85);
  return parsedThreshold > 1 ? parsedThreshold / 100 : parsedThreshold;
};

function BudgetDetailsModal({ budget, onClose, copy }) {
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);

  // Snapshot View State
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [snapshotDetails, setSnapshotDetails] = useState(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);

  // Icon resolver matching SpendingTransactionModal logic
  const getTransactionIconSrc = (transaction) =>
    transaction.logo_url || getInitialsIconSrc(transaction.merchant);

  // Load active cycle transactions
  useEffect(() => {
    async function loadTransactions() {
      if (!budget?.uuid || selectedSnapshot) return;
      setLoadingTx(true);
      const res = await fetchBudgetTransactions(budget.uuid);
      if (res.success) {
        setTransactions(res.data);
      }
      setLoadingTx(false);
    }
    loadTransactions();
  }, [budget?.uuid, selectedSnapshot]);

  // Handle Snapshot Click
  const handleSnapshotClick = async (barItem) => {
    if (!barItem?.uuid) return;

    setSelectedSnapshot(barItem);
    setLoadingSnapshot(true);
    setLoadingTx(true);

    const res = await fetchBudgetSnapshotDetails(barItem.uuid);
    if (res.success) {
      setSnapshotDetails(res.data);
      setTransactions(res.data.transactions || []);
    } else {
      setTransactions([]);
    }

    setLoadingTx(false);
    setLoadingSnapshot(false);
  };

  const handleBackToLiveBudget = async () => {
    setSelectedSnapshot(null);
    setSnapshotDetails(null);

    // Re-fetch current cycle live transactions
    if (budget?.uuid) {
      setLoadingTx(true);
      const res = await fetchBudgetTransactions(budget.uuid);
      if (res.success) {
        setTransactions(res.data);
      }
      setLoadingTx(false);
    }
  };

  // Determine active display data (Snapshot vs Live Budget)
  const isSnapshotView = Boolean(selectedSnapshot);
  const activeData = snapshotDetails?.snapshot || budget;

  // Extract snapshot (historical_) vs live budget fields
  const numericSpent = parseBudgetNumber(
    activeData?.historical_amount_spent ??
      activeData?.spent ??
      activeData?.amount_spent,
  );

  const numericLimit = parseBudgetNumber(
    activeData?.historical_amount_limit ??
      activeData?.limit ??
      activeData?.amount_limit,
    1,
  );

  const numericThreshold = normalizeThreshold(
    activeData?.historical_threshold ?? activeData?.threshold,
  );
  const thresholdLabel = Math.round(numericThreshold * 100);

  const currentStatus = activeData?.historical_status ?? activeData?.status;

  const currentFrequency =
    activeData?.historical_frequency ?? activeData?.frequency;

  const currentRelations =
    activeData?.historical_relations ?? activeData?.relations;

  const getStatusLabel = (status) => {
    switch (status) {
      case "SA":
      case "Save":
      case "GO":
      case "Good":
        return "Good";
      case "RI":
      case "At Risk":
        return "At Risk";
      case "PA":
      case "Passed":
        return "Passed";
    }
  };

  const getFrequencyLabel = (freq) => {
    switch (freq) {
      case "WE":
      case "Weekly":
        return "Weekly";
      case "MO":
      case "Monthly":
        return "Monthly";
      default:
        return freq || "Weekly";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Good":
      case "GO":
      case "Save":
      case "SA":
        return "status-save";
      case "At Risk":
      case "RI":
        return "status-at-risk";
      case "Passed":
      case "PA":
        return "status-passed";
      default:
        return "";
    }
  };

  // Safe list formatter for both live objects and snapshot relation arrays
  const formatList = (items, type) => {
    if (!items || items.length === 0) {
      return copy?.all_items?.replace("{type}", type);
    }
    return items
      .map((item) => {
        if (typeof item === "string") {
          const match = primarySpendingCategories.find((c) => c.value === item);
          return match ? match.label : item;
        }
        if (item.institution_name) return item.institution_name;
        if (item.name) return item.name;
        return "";
      })
      .filter((str) => str !== "")
      .join(" | ");
  };

  const currentInstitutions =
    activeData.institutions || currentRelations?.institution_ids;

  const currentAccounts = activeData.accounts || currentRelations?.account_ids;

  const currentCategories =
    activeData.categories || currentRelations?.categories;

  const rawThreshold = parseBudgetNumber(
    activeData?.historical_threshold ?? activeData?.threshold,
    90,
  );

  return (
    <Fragment>
      <ModalModern onClose={onClose}>
        <ModernPopupHeader onClose={onClose}>
          <div className="header-content-modern-row">
            <div>
              {isSnapshotView ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <button
                    onClick={handleBackToLiveBudget}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#3B82F6",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: "13px",
                      fontWeight: "600",
                      textAlign: "left",
                    }}
                  >
                    ← Back to Current Cycle
                  </button>
                  <h3 style={{ margin: 0 }}>
                    {budget.name}{" "}
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "normal",
                        color: "#6B7280",
                      }}
                    >
                      ({selectedSnapshot?.label})
                    </span>
                  </h3>
                </div>
              ) : (
                <>
                  <h3>{budget.name}</h3>
                  <div>
                    <span className="light-color medium-size">
                      {budget.description}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </ModernPopupHeader>

        <ModernPopupBody>
          <LoadingToRealTransitionWrapper
            isLoading={loadingSnapshot}
            ghost={
              <GhostCardContainer>
                <GhostBar width="100%" height="220px" />
              </GhostCardContainer>
            }
          >
            <div className="mp-col-container">
              {/* 1. PROGRESS FINISHED / CURRENT PROGRESS ACCORDION */}
              <CollapsibleCardContainer
                title={
                  isSnapshotView
                    ? "Progress Finished"
                    : copy?.current_progress || "Current Progress"
                }
                defaultOpen={true}
                gap="20px"
              >
                <div
                  className="budget-modal-card mobile-reset-standard-card-container"
                  style={{ width: "100%" }}
                >
                  <div className="bc-body">
                    <div>
                      <p className="bc-spent-row">
                        <span>${numericSpent}</span> spent from ${numericLimit}{" "}
                        limit
                      </p>
                    </div>
                    <div>
                      <ProgressBar
                        spent={numericSpent}
                        total={numericLimit}
                        threshold={rawThreshold}
                      />
                    </div>
                    <div className="bc-status-row">
                      <p>{copy?.status || "Status"}</p>
                      <span className={getStatusClass(currentStatus)}>
                        {getStatusLabel(currentStatus)}
                      </span>
                    </div>
                  </div>
                </div>
              </CollapsibleCardContainer>

              {/* 2. CYCLE TRANSACTIONS ACCORDION */}
              <CollapsibleCardContainer
                title={
                  isSnapshotView
                    ? "Snapshot Transactions"
                    : "Cycle Transactions"
                }
                defaultOpen={false}
                gap="20px"
              >
                <LoadingToRealTransitionWrapper
                  isLoading={loadingTx}
                  ghost={
                    <GhostCardContainer>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        <GhostBar width="100%" height="40px" />
                        <GhostBar width="100%" height="40px" />
                      </div>
                    </GhostCardContainer>
                  }
                >
                  {transactions.length === 0 ? (
                    <div
                      style={{
                        padding: "16px 0",
                        color: "#6B7280",
                        fontSize: "14px",
                      }}
                    >
                      No transactions recorded for this cycle.
                    </div>
                  ) : (
                    <div style={{ width: "100%" }}>
                      {transactions.map((tx, index) => {
                        const merchantName =
                          tx.merchant_name || tx.description || "Transaction";

                        return (
                          <TransactionRowCard
                            key={tx.transaction_id || tx.id || index}
                            getTransactionIconSrc={getTransactionIconSrc}
                            transaction={{
                              id: tx.transaction_id || tx.id,
                              merchant: merchantName,
                              logo_url: tx.logo_url,
                              account: tx.account_name || tx.account,
                              last4: tx.account_mask || tx.last4 || tx.mask,
                              date: tx.date,
                              amount: Number(tx.amount || 0),
                            }}
                            showBorder={index !== transactions.length - 1}
                          />
                        );
                      })}
                    </div>
                  )}
                </LoadingToRealTransitionWrapper>
              </CollapsibleCardContainer>

              {/* 3. BUDGET HISTORY ACCORDION (ONLY SHOWN IN LIVE VIEW) */}
              {!isSnapshotView && (
                <CollapsibleCardContainer
                  title="Budget History"
                  defaultOpen={false}
                  gap="20px"
                >
                  <div style={{ width: "100%" }}>
                    <BudgetHistoryChart
                      budget={budget}
                      onSnapshotClick={handleSnapshotClick}
                    />
                  </div>
                </CollapsibleCardContainer>
              )}

              {/* 4. BUDGET SETTINGS ACCORDION */}
              <CollapsibleCardContainer
                title={isSnapshotView ? "Snapshot Settings" : "Budget Settings"}
                defaultOpen={false}
                gap="20px"
              >
                <div
                  className="budget-modal-card mobile-reset-standard-card-container"
                  style={{ width: "100%" }}
                >
                  <div className="bc-settings-row">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/institutions_light_grey.svg`}
                      alt={copy?.institution_icon_alt || "Institutions"}
                    />
                    <div className="mp-col-container" style={{ gap: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <p className="margin-reset">
                          {copy?.institutions || "Institutions"}:{" "}
                          <i>
                            <span>
                              {formatList(
                                currentInstitutions,
                                copy?.institutions || "Institutions",
                              )}
                            </span>
                          </i>
                        </p>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <p className="margin-reset">
                          {copy?.accounts || "Accounts"}:{" "}
                          <span>
                            {formatList(
                              currentAccounts,
                              copy?.accounts || "Accounts",
                            )}
                          </span>
                        </p>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <p className="margin-reset">
                          {copy?.categories || "Categories"}:{" "}
                          <span>
                            {formatList(
                              currentCategories,
                              copy?.categories || "Categories",
                            )}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bc-settings-row">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/frequency_light_grey.svg`}
                      alt={copy?.institution_icon_alt || "Frequency"}
                    />
                    <div className="mp-col-container" style={{ gap: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <p>{copy?.frequency || "Frequency"}</p>
                        <span>{getFrequencyLabel(currentFrequency)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bc-settings-row">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/icons/target_light_grey.svg`}
                      alt={copy?.institution_icon_alt || "Limit & Threshold"}
                    />
                    <div className="mp-col-container" style={{ gap: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <p>{copy?.limit_threshold || "Limit & Threshold"}</p>
                        <span>
                          {copy?.limit_threshold_value
                            ? copy.limit_threshold_value
                                .replace("{limit}", `$${numericLimit}`)
                                .replace("{threshold}", thresholdLabel)
                            : `Limit: $${numericLimit} | Threshold: ${thresholdLabel}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleCardContainer>
            </div>
          </LoadingToRealTransitionWrapper>
        </ModernPopupBody>
      </ModalModern>
    </Fragment>
  );
}

export default BudgetDetailsModal;
